import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseHttpClient } from './http-client';
import { ConfigService } from './config.service';

/**
 * DTOs matching backend models
 */
export interface TenantGroupDto {
  groupID?: number;
  groupName: string;
  description?: string;
  isActive: boolean;
  createdDate?: string;
  createdBy?: number;
  modifiedDate?: string;
  modifiedBy?: number;
  members?: TenantGroupMemberDto[];
}

export interface TenantGroupMemberDto {
  memberID?: number;
  groupID?: number;
  domainID: number;  // ← ADDED: Required for delete operation
  domainName: string;
  clientDatabase: string;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
  createdDate?: string;
  createdBy?: number;
  modifiedDate?: string;
  modifiedBy?: number;
}

/**
 * Response from GetGroupMembers endpoint - includes current members AND all available domains
 */
export interface GroupMembersResponse {
  currentMembers: TenantGroupMemberDto[];
  allDomains: DomainDto[];
}

/**
 * Domain DTO from wv_Domain table (SystemAdmin.DomainDto)
 */
export interface DomainDto {
  domainID: number;
  domainName: string;
  databaseName: string;  // ← Backend: DatabaseName → JSON: databaseName
}

export interface CreateTenantGroupRequest {
  groupName: string;
  description?: string;
  isActive: boolean;
  members?: TenantGroupMemberDto[];
}

export interface UpdateTenantGroupRequest {
  groupName: string;
  description?: string;
  isActive: boolean;
}

/**
 * Request for adding multiple tenants to group (batch operation)
 */
export interface AddTenantsToGroupRequest {
  domains: DomainMemberRequest[];
}

/**
 * Domain member info from UI (includes domainName to avoid backend database lookup)
 */
export interface DomainMemberRequest {
  domainID: number;
  domainName: string;
}

export interface UpdateMemberSortOrderRequest {
  sortOrder: number;
}

/**
 * Service for SystemAdmin to manage tenant groups
 * Used for internal administration only - requires SystemAdmin privileges
 */
@Injectable({
  providedIn: 'root'
})
export class AdminTenantGroupService {
  private apiUrl: string;

  constructor(
    private http: BaseHttpClient,
    private configService: ConfigService
  ) {
    this.apiUrl = `${this.configService.get('apiBaseUrl')}/AdminTenantGroup`;
  }

  /**
   * Get all tenant groups
   */
  getAllGroups(): Observable<any> {
    return this.http.get(`${this.apiUrl}`).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Get tenant group by ID
   */
  getGroup(groupId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/${groupId}`).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Get members of a tenant group
   */
  getGroupMembers(groupId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/${groupId}/members`).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Create new tenant group
   */
  createGroup(request: CreateTenantGroupRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}`, request).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Update tenant group
   */
  updateGroup(groupId: number, request: UpdateTenantGroupRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/${groupId}`, request).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Delete tenant group (soft delete)
   */
  deleteGroup(groupId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/delete/${groupId}`).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Add member to tenant group
   */
  addMember(groupId: number, domains: any[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/${groupId}/members`, { domains }).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Remove member from tenant group
   */
  removeMember(groupId: number, domainId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${groupId}/members/${domainId}`).pipe(
      map((response: any) => response)
    );
  }
}
